/**
 * User Details Dialog Component
 * Shows read-only detailed information about a user
 *
 * @module Features/UserManagement/Dialogs
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Paper,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useGetUserByIdQuery } from '../Service/usersApi';
import { closeDetailsDialog } from '../slices/userManagementSlice';

interface UserDetailsDialogProps {
  userId: number;
}

/**
 * Format date for display
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

/**
 * User Details Dialog Component
 */
const UserDetailsDialog: React.FC<UserDetailsDialogProps> = ({ userId }) => {
  const dispatch = useDispatch();
  const { data: user, isLoading } = useGetUserByIdQuery(userId);

  const handleClose = () => {
    dispatch(closeDetailsDialog());
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: 600 }}>Szczegóły użytkownika</DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={40} />
          </div>
        ) : user ? (
          <Stack spacing={2}>
            {/* Basic info */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Podstawowe informacje
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, width: '120px' }}>Email:</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{user.email}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Imię i nazwisko:</TableCell>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>ID:</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{user.id}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>

            {/* Roles */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Role
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {user.roles.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    color={role === 'Admin' ? 'error' : 'primary'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>

            {/* Account status */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Status konta
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Aktywne:</TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Tak' : 'Nie'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Email zweryfikowany:</TableCell>
                    <TableCell>
                      <Chip
                        label={user.emailConfirmed ? 'Tak' : 'Nie'}
                        color={user.emailConfirmed ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Musi zmienić hasło:</TableCell>
                    <TableCell>
                      <Chip
                        label={user.mustChangePassword ? 'Tak' : 'Nie'}
                        color={user.mustChangePassword ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Nieudane próby logowania:</TableCell>
                    <TableCell>{user.failedLoginAttempts}</TableCell>
                  </TableRow>
                  {user.lockedOutUntil && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Zablokowane do:</TableCell>
                      <TableCell>{formatDate(user.lockedOutUntil)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            {/* Timestamps */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Aktywność
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Utworzono:</TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Zaktualizowano:</TableCell>
                    <TableCell>{formatDate(user.updatedAt)}</TableCell>
                  </TableRow>
                  {user.lastLoginAt && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Ostatnie logowanie:</TableCell>
                      <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        ) : (
          <Typography variant="body2" color="error">
            Nie udało się wczytać szczegółów użytkownika
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
